import { useEffect, useState, useRef } from 'react'
import axios from 'axios'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const INITIAL = {
  firstName: '',
  lastName: '',
  workEmail: '',
  company: '',
  jobTitle: '',
  marketingOptIn: true,
}

/**
 * Gated download modal. Captures downloader details, POSTs to
 * /api/webform/submit, then opens the resource PDF in a new tab.
 * Props: { resource: { slug, title, kind }, onClose: fn }
 */
export default function ResourceModal({ resource, onClose }) {
  const [form, setForm] = useState(INITIAL)
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('')
  const [downloadUrl, setDownloadUrl] = useState('')
  const dialogRef = useRef(null)

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    dialogRef.current?.focus()
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey) }
  }, [onClose])

  const onChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMsg('')
    try {
      const payload = {
        formType: 'Resource Download',
        ...form,
        resourceSlug: resource.slug,
        resourceTitle: resource.title,
      }
      await axios.post(`${API}/webform/submit`, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      const url = resource.file || `/resources/${resource.slug}.pdf`
      setDownloadUrl(url)
      setStatus('success')
      // trigger download / open in new tab
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      a.click()
    } catch (err) {
      const detail = err?.response?.data?.detail
      setErrorMsg(
        Array.isArray(detail) ? detail.map((d) => d?.msg || 'Invalid input').join(', ')
        : typeof detail === 'string' ? detail
        : 'Something went wrong. Please try again or email daniel.benson@tmrglobal.com.',
      )
      setStatus('error')
    }
  }

  return (
    <div className="qc-modal-backdrop" onClick={onClose} data-testid="resource-modal-backdrop">
      <div
        className="qc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="resource-modal-title"
        tabIndex={-1}
        ref={dialogRef}
        data-testid="resource-modal"
      >
        <button className="qc-modal-close" onClick={onClose} aria-label="Close" data-testid="resource-modal-close">×</button>

        {status !== 'success' ? (
          <>
            <span className="qc-resource-kind">{resource.kind}</span>
            <h3 id="resource-modal-title" className="qc-modal-title">{resource.title}</h3>
            <p className="qc-modal-sub">Tell us where to send it — one click and it&apos;s yours.</p>
            <form onSubmit={onSubmit} noValidate data-testid="resource-form">
              <div className="qc-form-row">
                <div className="qc-field">
                  <label htmlFor="r-firstName">First name</label>
                  <input id="r-firstName" name="firstName" required value={form.firstName} onChange={onChange} placeholder="Jane" data-testid="resource-firstName" />
                </div>
                <div className="qc-field">
                  <label htmlFor="r-lastName">Last name</label>
                  <input id="r-lastName" name="lastName" required value={form.lastName} onChange={onChange} placeholder="Whitmore" data-testid="resource-lastName" />
                </div>
              </div>
              <div className="qc-form-row">
                <div className="qc-field">
                  <label htmlFor="r-workEmail">Work email</label>
                  <input id="r-workEmail" name="workEmail" type="email" required value={form.workEmail} onChange={onChange} placeholder="jane@company.com" data-testid="resource-workEmail" />
                </div>
                <div className="qc-field">
                  <label htmlFor="r-jobTitle">Role</label>
                  <input id="r-jobTitle" name="jobTitle" value={form.jobTitle} onChange={onChange} placeholder="Head of Risk" data-testid="resource-jobTitle" />
                </div>
              </div>
              <div className="qc-form-row">
                <div className="qc-field full">
                  <label htmlFor="r-company">Company</label>
                  <input id="r-company" name="company" required value={form.company} onChange={onChange} placeholder="Acme Financial" data-testid="resource-company" />
                </div>
              </div>
              <label className="qc-checkbox">
                <input type="checkbox" name="marketingOptIn" checked={form.marketingOptIn} onChange={onChange} data-testid="resource-optin" />
                <span>Send me occasional QC4 insights and product updates. Unsubscribe anytime.</span>
              </label>
              <button
                type="submit"
                className="qc-btn qc-btn-primary qc-modal-submit"
                disabled={status === 'submitting'}
                data-testid="resource-submit"
              >
                {status === 'submitting' ? 'Preparing your download…' : 'Get the PDF'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v14"/><path d="m6 13 6 6 6-6"/><path d="M5 21h14"/></svg>
              </button>
              <p className="qc-form-consent" style={{marginTop: 12}}>
                We&apos;ll send it to your inbox and your download will start immediately.
              </p>
              {status === 'error' && <div className="qc-form-error" data-testid="resource-error">{errorMsg}</div>}
            </form>
          </>
        ) : (
          <div className="qc-modal-success" data-testid="resource-success">
            <div className="qc-modal-check" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m20 6-11 11-5-5"/></svg>
            </div>
            <h3 className="qc-modal-title">Your download has started</h3>
            <p className="qc-modal-sub">
              If nothing happened,{' '}
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" style={{color: 'var(--accent)', textDecoration: 'underline'}} data-testid="resource-download-link">
                click here to open “{resource.title}”
              </a>.
            </p>
            <button className="qc-btn qc-btn-outline" onClick={onClose} data-testid="resource-modal-done" style={{marginTop: 8}}>Close</button>
          </div>
        )}
      </div>
    </div>
  )
}
